import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import DataPill from "@components/pills/DataPill";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { userListsApis } from "@services/apis/UserLists";
import {
  GetUserListsResponse,
  KycStatus,
  UserItem,
} from "@services/models/UserLists/GetUserListsModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type BinaryFlag = 0 | 1;
const rowsPerPage = 10000;

const UsersListFlow: React.FC = () => {
  const gridRef = useRef<any>(null);
  const [records, setRecords] = useState<UserItem[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [isDebitFrozenFilter, setIsDebitFrozenFilter] = useState<
    "" | "Frozen" | "Not Frozen"
  >("");
  const [kycStatus, setKycStatus] = useState<"" | KycStatus>("");

  // Pagination
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Helper function for Yes/No display
  const yesNo = (v: BinaryFlag | number) => (Number(v) === 1 ? "YES" : "NO");

  // AG-Grid Column Definitions
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "User ID",
        field: "userId",
        flex: 0.8,
        minWidth: 70,
        maxWidth: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1">{params.data.userId}</span>
              <CopyButton
                copyText={
                  params.data.userId ? params.data.userId.toString() : ""
                }
                copyMessage="User ID copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Email ID",
        field: "emailId",
        flex: 1.8,
        minWidth: 160,
        maxWidth: 240,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <div className="flex items-center justify-between w-full">
              <span
                className="flex-1 truncate pr-1 max-w-[180px]"
                title={data.emailId || "-"}
              >
                {data.emailId || "-"}
              </span>
              <CopyButton
                copyText={data.emailId ? data.emailId : ""}
                copyMessage="Email copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "User Name",
        field: "username",
        flex: 1.3,
        minWidth: 120,
        maxWidth: 170,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return data.username || "-";
        },
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Account Type",
        field: "accountType",
        flex: 1.1,
        minWidth: 100,
        maxWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return data.accountType.toUpperCase() || "-";
        },
      },
      {
        headerName: "WhatsApp",
        field: "whatsappNumber",
        flex: 1.2,
        minWidth: 110,
        maxWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return data.whatsappNumber || "-";
        },
      },
      {
        headerName: "Frozen",
        field: "isDebitFrozen",
        flex: 0.7,
        minWidth: 70,
        maxWidth: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <DataPill
              label={yesNo(data.isDebitFrozen)}
              tone={data.isDebitFrozen === 0 ? "green" : "red"}
            />
          );
        },
      },
      {
        headerName: "KYC ID",
        field: "idStatus",
        flex: 0.7,
        minWidth: 70,
        maxWidth: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return <DataPill value={data.idStatus} />;
        },
      },
      {
        headerName: "KYC Address",
        field: "addressStatus",
        flex: 1,
        minWidth: 80,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return <DataPill value={data.addressStatus} />;
        },
      },
      {
        headerName: "Balance",
        field: "totalBalance",
        flex: 1.1,
        minWidth: 100,
        maxWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (props: any) => {
          const { data } = props;
          return Number(data.totalBalance).toLocaleString(undefined, {
            maximumFractionDigits: 8,
          });
        },
        comparator: (valueA: string, valueB: string) => {
          return Number(valueA) - Number(valueB);
        },
      },
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1.6,
        minWidth: 160,
        maxWidth: 240,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
    ],
    [],
  );

  const onBodyScroll = useCallback(
    (params: any) => {
      const scrollTop = params.top;
      const scrollHeight = params.api.getDisplayedRowCount() * 48; // row height
      const clientHeight =
        params.api.getVerticalPixelRange().bottom -
        params.api.getVerticalPixelRange().top;

      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        hasMore &&
        !isLoading
      ) {
        loadMoreUsers();
      }
    },
    [hasMore, isLoading],
  );

  const buildParams = useCallback(
    (pageNum: number) => ({
      search: appliedSearchTerm.trim() || undefined,
      isDebitFrozen:
        isDebitFrozenFilter === ""
          ? undefined
          : ((isDebitFrozenFilter === "Frozen" ? 1 : 0) as BinaryFlag),
      kycStatus: kycStatus || undefined,
      page: pageNum,
      rows: rowsPerPage,
      accountType: "cashier",
    }),
    [appliedSearchTerm, isDebitFrozenFilter, kycStatus],
  );

  const fetchUsers = useCallback(
    async (pageNum: number, append: boolean) => {
      try {
        setIsLoading(true);
        setError("");

        const params = buildParams(pageNum);
        const resp: GetUserListsResponse =
          await userListsApis.getUsersList(params);

        const fetched = resp.data?.users ?? [];
        const rows = resp.data?.rows ?? rowsPerPage;

        setRecords((prev) => (append ? [...prev, ...fetched] : fetched));

        // ✅ Only this: if we got a full page, assume there may be more
        setHasMore(fetched.length === rows);
      } catch (err: any) {
        setError(err?.message ?? "Failed to fetch users.");
        // Stop further requests on error
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    },
    [buildParams],
  );

  // Reset + refetch when filters / applied search change
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchUsers(1, false);
  }, [isDebitFrozenFilter, kycStatus, appliedSearchTerm, fetchUsers]);

  // Load more when page increments
  useEffect(() => {
    if (page > 1 && hasMore) fetchUsers(page, true);
  }, [page, hasMore, fetchUsers]);

  // Load more (guarded) — functional page update
  const loadMoreUsers = useCallback(() => {
    if (isLoading || !hasMore) return;
    setPage((prev) => {
      const next = prev + 1;
      fetchUsers(next, true);
      return next;
    });
  }, [isLoading, hasMore, fetchUsers]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();

    if (normalizedSearchTerm === appliedSearchTerm) {
      setPage(1);
      setHasMore(true);
      fetchUsers(1, false);
      return;
    }

    setAppliedSearchTerm(normalizedSearchTerm);
  }, [appliedSearchTerm, fetchUsers, searchTerm]);

  const handleClearSearch = useCallback(() => {
    if (!searchTerm && !appliedSearchTerm) {
      return;
    }

    setSearchTerm("");

    if (appliedSearchTerm === "") {
      setPage(1);
      setHasMore(true);
      fetchUsers(1, false);
      return;
    }

    setAppliedSearchTerm("");
  }, [appliedSearchTerm, fetchUsers, searchTerm]);

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px]">
        <div className="flex flex-row justify-between items-center w-full mr-4">
          <p className="text-text-base text-[14px] font-semibold">Users</p>
        </div>
      </div>

      <div
        className="flex flex-row"
        style={{ height: "calc(100dvh - 120px)" }}
      >
        <div className="flex flex-col bg-bg-page-body py-[20px] w-full h-full overflow-hidden p-4">
          {/* Filters + Search Row */}
          <div className="flex flex-row items-center justify-between mb-4">
            <div className="flex flex-row flex-wrap items-center gap-4">
              {/* isDebitFrozen chips (mutually exclusive) */}
              <div className="flex flex-col mb-2">
                <p className="text-text-base text-[10px] font-bold mb-1">
                  Frozen Status
                </p>
                <div className="flex space-x-2">
                  {["Frozen", "Not Frozen"].map((label) => (
                    <div
                      key={label}
                      onClick={() =>
                        setIsDebitFrozenFilter((prev) =>
                          prev === label
                            ? ""
                            : (label as "Frozen" | "Not Frozen"),
                        )
                      }
                      className={`cursor-pointer border py-[4px] px-[12px] text-[10px] font-medium rounded-[100px] ${
                        isDebitFrozenFilter === label
                          ? "border-border-button-secondary bg-bg-button-secondary"
                          : "border-border-subtle bg-bg-page-body"
                      }`}
                      title={`Show only ${label.toLowerCase()} users`}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              {/* kycStatus dropdown */}
              <div className="flex flex-col">
                <p className="text-text-base text-[10px] font-bold mb-1">
                  KYC Status
                </p>
                <select
                  value={kycStatus}
                  onChange={(e) =>
                    setKycStatus(e.target.value as "" | KycStatus)
                  }
                  className="py-2 px-2 text-[12px] rounded-md border border-border-subtle focus:outline-none"
                >
                  <option value="">All</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>

            <GlobalSearchInputField
              value={searchTerm}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              disabled={isLoading}
              isSearchApplied={Boolean(appliedSearchTerm)}
              placeholder="Search by User ID, Email ID"
            />
          </div>

          {/* Table */}
          {isLoading && records.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              className="border border-border-subtle rounded-lg overflow-hidden"
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={records}
              columnDefs={columnDefs}
              onBodyScroll={onBodyScroll}
              getRowId={(params) => params.data.userId.toString()}
              error={error}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default UsersListFlow;
